import { AssessmentType, AttachmentCategory, SurveyStatus, UserFeatureFlag, UserProfileRole } from '@@types/api.types';
import { DropdownMenuComponent, DropdownMenuItemType } from '@components/core/dropdown-menu.component';
import { IconButton } from '@components/core/icon-button.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { SimpleTabs } from '@components/shared/simple-tabs.component';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { Skeleton } from '@components/ui/skeleton';
import { useSurveyNavigation } from '@hooks/surveys.hook';
import { errorToast } from '@hooks/toast.hook';
import { useGetTechnologyAttachments } from '@queries/attachments.query';
import { useGetCategories } from '@queries/category.query';
import { useGetSurveys, useInnovatorCreateSurvey } from '@queries/survey.query';
import {
  useGetTechnology,
  useGetTechnologyMrlAssessment,
  useGetTechnologyTrlAssessment,
} from '@queries/technology.query';
import { useGetCurrentUser } from '@queries/users.query';
import { isChildOrganizationView as getIsChildOrganizationView } from '@utils/parent-organization.util';
import { TechnologyHeaderTab } from '@utils/query-state.util';
import { formatProper } from '@utils/text.util.ts';
import {
  Building2Icon,
  CogIcon,
  EllipsisVertical,
  FileTextIcon,
  GlobeLockIcon,
  ImageIcon,
  LayersIcon,
  ShieldIcon,
  TargetIcon,
} from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { scrollToNavOrTop } from './shared/scroll.utils';
import { canReadTechnologyCyberTab } from './technology-cyber-tab.util';
import { TechnologyTechnologyModal } from './technology-technology-modal.component';

export const defaultTab: TechnologyHeaderTab = 'overview';

// Tab order: Overview, Details, Organization, TRL, MRL, Mission, Cyber
const TAB_ORDER: TechnologyHeaderTab[] = ['overview', 'details', 'organization', 'trl', 'mrl', 'ma', 'cyber'];

const tabLabels: Record<TechnologyHeaderTab, string> = {
  cyber: 'Cybersecurity',
  details: 'Details',
  ma: 'Mission',
  mrl: 'MRL',
  organization: 'Organization',
  overview: 'Overview',
  trl: 'TRL',
};

type TechnologyHeaderProps = {
  onToggleTab?: (tab: TechnologyHeaderTab) => void;
  selectedTab?: TechnologyHeaderTab;
};

export const TechnologyHeader: React.FC<TechnologyHeaderProps> = (props) => {
  const { onToggleTab, selectedTab } = props;
  const { technologyId } = useParams();
  const { navigateToSurvey } = useSurveyNavigation();
  const headerNavAnchorRef = useRef<HTMLDivElement>(null);

  const { data: technology, isPending: isTechnologyLoading } = useGetTechnology(technologyId);
  const { data: technologyImages = [] } = useGetTechnologyAttachments({
    category: AttachmentCategory.TECHNOLOGY_IMAGE,
    technologyId: technologyId ?? '',
  });
  const { data: categories = [] } = useGetCategories();
  const { data: user, isSelectedCompanyParentOrganization, selectedCompany } = useGetCurrentUser();
  const { data: trlAssessment } = useGetTechnologyTrlAssessment(technologyId);
  const { data: mrlAssessment } = useGetTechnologyMrlAssessment(technologyId);
  const { data: draftSurveys = [] } = useGetSurveys({
    status: SurveyStatus.DRAFT,
    technologyId,
  });
  const { mutateAsync: createSurvey } = useInnovatorCreateSurvey();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const isInnovator = user?.profile?.role === UserProfileRole.INNOVATOR;

  const tabIcons: Record<TechnologyHeaderTab, React.ReactNode> = {
    cyber: <GlobeLockIcon size={16} />,
    details: <LayersIcon size={16} />,
    ma: <TargetIcon size={16} />,
    mrl: <CogIcon size={16} />,
    organization: <Building2Icon size={16} />,
    overview: <FileTextIcon size={16} />,
    trl: <ShieldIcon size={16} />,
  };

  const isChildOrganizationView = getIsChildOrganizationView({
    isSelectedCompanyParentOrganization,
    selectedCompanyId: selectedCompany?.id,
    technologyCompanyId: technology?.company?.id,
  });
  const canEdit = user?.profile?.role === UserProfileRole.INNOVATOR && !isChildOrganizationView;
  const canReadCyber = canReadTechnologyCyberTab({
    hasCyberAssessmentsTool: user?.featureFlags?.includes(UserFeatureFlag.CYBER_ASSESSMENTS_TOOL) ?? false,
    isChildOrganizationView,
    role: user?.profile?.role,
  });
  const featuredTechnologyImage = technologyImages.find((technologyImage) => technologyImage.featured);

  const visibleTabOrder = useMemo(
    () =>
      TAB_ORDER.filter((tab) => {
        if (tab === 'cyber') return canReadCyber;
        if (tab === 'organization') return !isInnovator;
        return true;
      }),
    [canReadCyber, isInnovator],
  );

  const tabs = visibleTabOrder.map((key) => ({
    icon: tabIcons[key],
    label: tabLabels[key],
    value: key,
  }));

  const handleTabChange = (value: string) => {
    scrollToNavOrTop({ anchorElement: headerNavAnchorRef.current, stackedBehavior: 'top' });
    onToggleTab?.(value as TechnologyHeaderTab);
  };

  const subcategories = categories.flatMap((category) => category.subcategories);
  const subcategory = subcategories.find((item) => item.id === technology?.subcategoryId);

  const draftTrlSurvey = draftSurveys.find(
    (survey) => survey.technology?.id === technologyId && survey.assessmentType === AssessmentType.TRL,
  );
  const draftMrlSurvey = draftSurveys.find(
    (survey) => survey.technology?.id === technologyId && survey.assessmentType === AssessmentType.MRL,
  );

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleStartTrl = async () => {
    if (!technologyId || !technology) return;

    if (draftTrlSurvey) {
      navigateToSurvey(`/technology/${technologyId}/survey/${draftTrlSurvey.id}`);
      return;
    }

    try {
      const survey = await createSurvey({
        assessmentType: AssessmentType.TRL,
        technologyId,
      });
      navigateToSurvey(`/technology/${technologyId}/survey/${survey.id}`);
    } catch {
      errorToast({ description: 'Failed to create TRL assessment. Please try again.' });
    }
  };

  const handleStartMrl = async () => {
    if (!technologyId || !technology) return;

    if (draftMrlSurvey) {
      navigateToSurvey(`/technology/${technologyId}/survey/${draftMrlSurvey.id}`);
      return;
    }

    try {
      const survey = await createSurvey({
        assessmentType: AssessmentType.MRL,
        technologyId,
      });
      navigateToSurvey(`/technology/${technologyId}/survey/${survey.id}`);
    } catch {
      errorToast({ description: 'Failed to create MRL assessment. Please try again.' });
    }
  };

  const menuItems: DropdownMenuItemType[] = [];

  if (canEdit) {
    menuItems.push({
      onClick: handleOpenModal,
      text: 'Edit Details',
    });

    menuItems.push({
      onClick: handleStartTrl,
      text: draftTrlSurvey ? 'Resume TRL Assessment' : trlAssessment?.score ? 'Revise TRL Assessment' : 'Start TRL',
    });

    menuItems.push({
      onClick: handleStartMrl,
      text: draftMrlSurvey ? 'Resume MRL Assessment' : mrlAssessment?.score ? 'Revise MRL Assessment' : 'Start MRL',
    });
  }

  return (
    <>
      <div
        className="sticky z-20 overflow-x-hidden top-(--impersonation-banner-height,0px) flex flex-col pt-4 -mt-4 -mx-4 w-[calc(100%+2rem)] bg-white border-b border-gray-200 shadow-sm before:absolute before:inset-0 before:bg-white before:-z-10 before:left-1/2 before:-translate-x-1/2 before:w-svw"
        data-tech-header="true"
      >
        <div
          aria-hidden="true"
          className="h-0"
          ref={headerNavAnchorRef}
        />

        {/* Header Content */}
        <div className="@container/technology-header mx-auto w-full max-w-[1600px] px-4">
          <div className="pb-4 pt-2 w-full">
            {isTechnologyLoading ? (
              <div className="flex flex-row gap-4 items-center w-full">
                <Skeleton className="size-20 min-w-[80px] rounded-lg" />
                <div className="flex flex-col gap-2 w-full justify-between py-1">
                  <Skeleton className="h-7 w-48" />
                  <Skeleton className="h-5 w-32 rounded-full" />
                  <Skeleton className="h-4 w-40" />
                </div>
              </div>
            ) : (
              <div className="flex flex-row gap-4 w-full group">
                <div className="size-20 overflow-hidden rounded-lg bg-gray-100 shrink-0 flex items-center justify-center">
                  {featuredTechnologyImage?.inlineUrl ? (
                    <img
                      alt={`${technology?.name ?? 'Technology'} featured image`}
                      className="size-full object-cover"
                      src={featuredTechnologyImage.inlineUrl}
                    />
                  ) : (
                    <ImageIcon
                      className="text-gray-400"
                      size={28}
                    />
                  )}
                </div>
                <div className="flex gap-3 w-full py-1">
                  <div className="flex flex-row gap-4 justify-between items-start w-full">
                    <div className="flex flex-col">
                      <div className="text-xl font-medium text-gray-800">{technology?.name}</div>
                      <BlueLabel text={`${formatProper(technology?.type)} Technology`} />
                      <div className="flex gap-1 mt-1 items-start text-xs text-gray-500">
                        {technology?.category && (
                          <div className="flex items-start gap-1">
                            {technology.category.description ? (
                              <TooltipComponent
                                side="right"
                                className="text-xs whitespace-pre-wrap font-normal"
                                trigger={
                                  <span className="font-medium cursor-help border-b border-dashed border-gray-300">
                                    {technology.category.name}
                                  </span>
                                }
                              >
                                <div>
                                  <div className="font-medium">Category</div>
                                  {technology.category.description}
                                </div>
                              </TooltipComponent>
                            ) : (
                              <span className="font-medium">{technology.category.name}</span>
                            )}
                          </div>
                        )}
                        {subcategory && (
                          <div className="flex items-center gap-2">
                            {subcategory.description ? (
                              <TooltipComponent
                                side="right"
                                className="text-xs whitespace-pre-wrap font-normal"
                                trigger={
                                  <span className="font-medium cursor-help text-gray-700">{`– ${subcategory.name}`}</span>
                                }
                              >
                                {subcategory.description}
                              </TooltipComponent>
                            ) : (
                              <span className="font-medium">{`– ${subcategory.name}`}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {canEdit && menuItems.length > 0 && (
                      <DropdownMenuComponent
                        items={menuItems}
                        trigger={
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <IconButton.HoverReveal
                              ariaLabel="Open actions menu"
                              icon={<EllipsisVertical />}
                              variant="secondary"
                            />
                          </div>
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="pb-5 @min-[62.5rem]/technology-header:hidden">
            <Select
              onValueChange={handleTabChange}
              value={selectedTab ?? defaultTab}
            >
              <SelectTrigger className="w-full bg-white">
                <SelectValue placeholder="Select tab">
                  {tabs.find((tab) => tab.value === (selectedTab ?? defaultTab))?.label ?? tabLabels[defaultTab]}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {tabs.map((tab) => (
                  <SelectItem
                    key={tab.value}
                    value={tab.value}
                  >
                    {tab.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="hidden overflow-x-auto overscroll-x-contain touch-pan-x @min-[62.5rem]/technology-header:block">
            <SimpleTabs
              activeTab={selectedTab ?? defaultTab}
              className="min-w-max"
              fullWidth={false}
              onTabChange={handleTabChange}
              resetScrollOnNavigation={false}
              tabs={tabs}
              variant="attached"
            />
          </div>
        </div>
      </div>

      {technology && canEdit && (
        <TechnologyTechnologyModal
          isEdit={true}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          technology={technology}
        />
      )}
    </>
  );
};
